// src/components/Table.js
import React, { Fragment } from 'react';

/**
 * Reusable, responsive data table. All styling lives in App.css (.tbl-*).
 *
 * Responsive: wrapped in a horizontally-scrollable container with a `minWidth`,
 * so on narrow screens columns keep size and the user scrolls sideways.
 *
 * Props:
 *   columns   Array of column configs:
 *     { key, header (string|node|fn), render(row,i), align('left'|'center'|'right'),
 *       width, nowrap, onClick(row,e), thStyle, tdStyle }
 *   data           Array of row objects
 *   rowKey         (row, index) => key   (defaults to index)
 *   onRowClick     (row) => void         (optional — whole-row click)
 *   rowStyle       (row, index) => style (optional — e.g. selected highlight; inline by design)
 *   emptyMessage   shown when data is empty (string | ReactNode)
 *   minWidth       min table width before horizontal scroll kicks in (default 720)
 *   maxHeight      cap body height + scroll vertically inside the table (px or CSS string)
 *   stickyHeader   force a sticky header (auto-enabled when maxHeight is set)
 *   renderExpanded (row, index) => ReactNode | null — full-width detail row when truthy
 */
export default function Table({
  columns,
  data = [],
  rowKey,
  onRowClick,
  rowStyle,
  emptyMessage = 'No records found',
  minWidth = 720,
  maxHeight,
  stickyHeader = false,
  renderExpanded,
}) {
  const keyOf = (row, i) => (rowKey ? rowKey(row, i) : i);
  const sticky = stickyHeader || maxHeight != null;

  const cellClass = (c) => [
    c.align === 'center' && 'tbl-cell--center',
    c.align === 'right' && 'tbl-cell--right',
    c.nowrap && 'tbl-cell--nowrap',
    c.onClick && 'tbl-cell--clickable',
  ].filter(Boolean).join(' ');

  return (
    <div
      className="tbl-scroll"
      style={maxHeight != null ? { maxHeight, overflowY: 'auto' } : undefined}
    >
      <table className="tbl" style={{ minWidth }}>
        <thead>
          <tr>
            {columns.map((c, ci) => (
              <th
                key={c.key ?? ci}
                className={sticky ? 'is-sticky' : undefined}
                style={{ ...(c.width ? { width: c.width } : {}), ...c.thStyle }}
              >
                {typeof c.header === 'function' ? c.header() : c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((row, i) => {
            const k = keyOf(row, i);
            const extra = rowStyle ? rowStyle(row, i) : null;
            const expanded = renderExpanded ? renderExpanded(row, i) : null;
            return (
              <Fragment key={k}>
                <tr
                  className={`tbl-row${i % 2 ? '' : ' tbl-row--alt'}`}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{ ...(onRowClick ? { cursor: 'pointer' } : {}), ...extra }}
                >
                  {columns.map((c, ci) => (
                    <td
                      key={c.key ?? ci}
                      className={cellClass(c) || undefined}
                      onClick={c.onClick ? (e) => c.onClick(row, e) : undefined}
                      style={c.tdStyle}
                    >
                      {c.render ? c.render(row, i) : row[c.key]}
                    </td>
                  ))}
                </tr>
                {expanded && (
                  <tr>
                    <td colSpan={columns.length} className="tbl-expanded-cell">{expanded}</td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {data.length === 0 && <div className="tbl-empty">{emptyMessage}</div>}
    </div>
  );
}
